// Chapter 6 - Constructing and Interpreting Graphic Displays of Behavioral Data
// Source: Cooper, Heron, & Heward (3rd ed.), Chapter 6.
// All content traced to source-pdfs/Chapter-6-Constructing-and-Interpreting-Graphic-Displays-of-Behavioral-Data-5.pdf

import type { ChapterModule } from "./types";

const chapter06Module: ChapterModule = {
  meta: {
    id: "06",
    number: 6,
    title: "Constructing and Interpreting Graphic Displays of Behavioral Data",
    subtitle: "Line graphs, axes, conditions, visual analysis",
    part: "Part 3 - Evaluating and Analyzing Behavior Change",
    estMinutes: 75,
    bacb: ["C-10", "C-11"],
  },
  curated: true,
  overview: [
    "Chapter 6 covers how behavior analysts turn measurements into decisions: the visual analysis of graphed data. Graphs are simple two-dimensional formats for displaying relationships among a series of measurements. In ABA they replace null-hypothesis statistical testing as the primary judgmental aid. Visual analysis is conservative: only variables that produce meaningful effects REPEATEDLY survive.",
    "You will learn the parts of a line graph (x-axis, y-axis, condition change lines, condition labels, data points, data path, figure caption), special graphs (bar, cumulative record, Standard Celeration Chart, scatterplot), the rules for constructing line graphs (axis lengths, scale breaks, connecting/not-connecting data points), and the four properties that drive within-condition visual analysis (number of data points, variability, level, trend).",
  ],
  objectives: [
    { id: "C-10", text: "Graph data to communicate relevant quantitative relations (line graph, bar graph, cumulative record, scatterplot)." },
    { id: "C-11", text: "Interpret graphed data using visual analysis of level, trend, variability, immediacy, and overlap." },
  ],
  coreConcepts: [
    {
      title: "Why graphs (not statistics)",
      body: [
        "Direct, repeated measurement produces data; graphs make that data analyzable in real time. Graphing each measure as it is collected allows immediate treatment decisions responsive to participant performance. Visual analysis is fast, easy to learn, imposes no arbitrary alpha, and screens out weak or unstable effects.",
      ],
      bullets: [
        "Graphs allow direct and continual contact with data while a study runs.",
        "Graphs enable independent judgments of meaning by other readers.",
        "Graphs can serve as feedback to the person whose behavior is depicted.",
        "Visual analysis is conservative: only repeatable, meaningful effects survive.",
      ],
    },
    {
      title: "Anatomy of a line graph",
      body: [
        "The line graph is the most common format in ABA, built on the Cartesian plane. Its major parts: horizontal axis (x), vertical axis (y), condition change lines, condition labels, data points, data path, and figure caption.",
      ],
      bullets: [
        "Vertical axis (y): length about two-thirds of the horizontal axis.",
        "Horizontal axis (x): equal intervals from left to right, each representing a successive equal time period.",
        "Condition change lines: vertical lines marking treatment changes, drawn to the height of the y-axis.",
        "Condition labels: brief descriptive text identifying each phase.",
        "Data points: bold, solid dots with different geometric symbols when paths overlap.",
        "Data path: straight lines connecting successive data points within a condition.",
        "Figure caption: concise complete description sufficient to interpret the display.",
      ],
    },
    {
      title: "Other graph types",
      body: [
        "Bar graphs display discrete data with no underlying dimension on the x-axis (e.g., comparing conditions). Cumulative records show total responses since measurement began; the slope of the data path equals response rate. Standard Celeration Charts use a multiply/divide y-axis (six cycles) so equal proportional changes look like equal vertical distances. Scatterplots show the distribution of individual measures relative to the x- and y-axes (often time-of-day vs occurrence).",
      ],
      bullets: [
        "Cumulative records are best when total responses matter, the behavior can occur only once per period, or feedback to the subject is the use case.",
        "Standard Celeration Chart (SCC) is the workhorse of Precision Teaching.",
        "Bar graphs summarize and compare conditions; line graphs trace change across time.",
      ],
    },
    {
      title: "Connecting (and not connecting) data points",
      body: [
        "Successive data points should NOT be connected when: (a) they fall on either side of a condition change line; (b) they span a significant unmeasured period; (c) they cross a discontinuity on the x-axis; (d) data were lost in a regularly scheduled measurement; (e) they fall in an unevenly spaced follow-up period; (f) one member of a pair falls outside the y-axis range.",
      ],
    },
    {
      title: "Within-condition visual analysis: 4 properties",
      body: [
        "When examining data inside a single condition, focus on (1) the number of data points, (2) the variability of performance, (3) the level of performance, and (4) the direction and degree of any trend.",
      ],
      bullets: [
        "More data + greater stability gives more confidence in the path's estimate.",
        "High variability inside a condition signals little or no control over the behavior.",
        "Level: the value the data hover around. Stable when measures cluster, variable when they don't.",
        "Trend: the overall direction (increasing, decreasing, zero), described by direction, degree, and variability around the line.",
      ],
    },
    {
      title: "Trend lines and split-middle method",
      body: [
        "Trend direction and degree are often summarized by a trend line. Three methods: freehand, least-squares regression, and split-middle line of progress. Split-middle is quick, reliable, and well-suited to applied work.",
      ],
    },
    {
      title: "Between-condition visual analysis: 5 dimensions",
      body: [
        "When comparing conditions, evaluate change in level, trend, variability, immediacy of effect, and overlap of data across condition boundaries.",
      ],
      bullets: [
        "Level change: did the central value shift? By how much?",
        "Trend change: did slope direction or steepness change?",
        "Variability change: did stability improve or degrade?",
        "Immediacy: how quickly did the effect appear after the condition change?",
        "Overlap: how many data points in one condition fall within the value range of the previous condition? Less overlap = stronger effect.",
      ],
    },
    {
      title: "Common graph-construction pitfalls",
      body: [
        "Before interpreting any graph, examine its construction. If distortion is suspected (truncated y-axis, missing scale breaks, blocked or averaged data), replot before drawing conclusions. Mean and median level lines obscure variability and should be added with care, not as a substitute for the data.",
      ],
    },
  ],
  glossary: [
    { term: "Graph", definition: "A relatively simple, two-dimensional format for visually displaying relationships among and between a series of measurements.", sourceHint: "Graphs are relatively simple formats for visually displaying relationships among and between a series of measurements." },
    { term: "Data", definition: "The product of measurement; the medium with which behavior analysts work.", sourceHint: "the product of those measurements is called data." },
    { term: "Data path", definition: "Straight lines connecting successive data points within a condition on a line graph.", sourceHint: "Data paths are created by connecting successive data points with a straight line." },
    { term: "Line graph", definition: "The most commonly used graphic format in ABA, based on the Cartesian plane.", sourceHint: "Line graphs, the most commonly used format for the graphic display of behavioral data, are based on the Cartesian plane." },
    { term: "Bar graph", definition: "Used to display discrete data not related by an underlying dimension OR to summarize and compare conditions.", sourceHint: "Bar graphs are used for two primary purposes: (a) to display discrete data not related by an underlying dimension that can be used to scale the horizontal axis and (b) to summarize and enable easy comparison." },
    { term: "Cumulative record", definition: "A graph in which each data point represents the total number of responses since measurement began; slope = response rate.", sourceHint: "Each data point on a cumulative record represents the total number of responses emitted by the subject since measurement began. The steeper the slope of the data path on a cumulative graph, the higher the response rate." },
    { term: "Cumulative recorder", definition: "A device (mechanical or electronic) that draws a cumulative record in real time.", sourceHint: "cumulative recorder (Key Terms list)." },
    { term: "Scatterplot", definition: "A graph that shows the distribution of individual measures with respect to the variables on the axes.", sourceHint: "A scatterplot shows the relative distribution of individual measures in a data set with respect to the variables depicted by the x- and y-axes." },
    { term: "Standard Celeration Chart", definition: "A six-cycle multiply-divide chart that depicts equal proportional changes as equal distances on the vertical axis.", sourceHint: "The Standard Celeration Chart is a six-cycle multiply-divide graph that enables the standardized charting of celeration." },
    { term: "Precision teaching", definition: "An instructional system that uses the Standard Celeration Chart and tracks rate of correct and incorrect responses across time.", sourceHint: "precision teaching (Key Terms list)." },
    { term: "Independent variable", definition: "What the experimenter manipulates (often the intervention).", sourceHint: "independent variable (Key Terms list)." },
    { term: "Dependent variable", definition: "The measured behavior (what changes in response to the IV).", sourceHint: "dependent variable (Key Terms list)." },
    { term: "Equal-interval scale", definition: "A scale where equal vertical distances reflect equal additive differences (the default y-axis in ABA line graphs).", sourceHint: "equal-interval scale (Key Terms list)." },
    { term: "Ratio scale", definition: "A scale on which equal proportional differences map to equal vertical distances (e.g., logarithmic / SCC).", sourceHint: "Ratio charts use a logarithmic-scaled y-axis so that changes in behavior that are of equal proportion (e.g., doublings of the response measure) are represented by equal distances on the vertical axis." },
    { term: "Overall response rate", definition: "Average response rate across an entire observation period.", sourceHint: "Overall response rate refers to the average rate of response over a given time period." },
    { term: "Local response rate", definition: "Response rate during a smaller period inside a larger period for which the overall rate has been given.", sourceHint: "a local response rate refers to the rate of response during a smaller period within a larger period." },
    { term: "Level", definition: "The value on the vertical axis around which a series of data points converges.", sourceHint: "Level refers to the value on the vertical axis around which a series of data points converges." },
    { term: "Variability", definition: "The frequency and degree to which multiple measures yield different outcomes.", sourceHint: "Variability refers to the frequency and degree to which multiple measures of behavior yield different outcomes." },
    { term: "Trend", definition: "The overall direction taken by a data path, described by direction, degree, and variability around the line.", sourceHint: "Trend refers to the overall direction taken by a data path; trends are described in terms of their direction (increasing, decreasing, or zero trend), degree (gradual or steep), and the extent of variability of data points around the trend." },
    { term: "Split-middle line of progress", definition: "A method for drawing a trend line quickly and reliably through a series of data points by dividing the data in half and aligning medians.", sourceHint: "Split-middle lines of progress can be drawn quickly and reliably and have proven useful in analyzing behavior change." },
    { term: "Visual analysis", definition: "Inspection of graphed data to determine whether a meaningful change occurred and whether it can be attributed to the independent variable.", sourceHint: "Visual analysis of graphed data attempts to answer two questions: (a) did a socially meaningful change in behavior take place and (b) if so, can the behavior change be attributed to the independent variable?" },
  ],
  procedures: [
    {
      title: "Construct a defensible line graph",
      intro: "Follow Cooper et al. conventions to avoid visual distortion.",
      steps: [
        { title: "Scale the axes", detail: "Vertical axis is about two-thirds the length of the horizontal axis." },
        { title: "Mark x-axis units", detail: "Equal intervals representing successive equal time periods (sessions, days). Use scale breaks for discontinuities." },
        { title: "Scale y-axis", detail: "Match the dimension measured, the observed range, and the socially significant change levels." },
        { title: "Draw condition change lines", detail: "Vertical lines at every intervention change, drawn to y-axis height. Add a short descriptive condition label above each phase." },
        { title: "Plot data points and connect", detail: "Solid dots; different shapes for multiple paths; connect within a condition but NEVER across condition lines, time discontinuities, or missing data periods." },
        { title: "Write the figure caption", detail: "Caption should be self-contained and complete enough to interpret the graph without reading the body text." },
      ],
    },
    {
      title: "Within-condition visual analysis (the 4-point check)",
      intro: "Run this for every condition before comparing across conditions.",
      steps: [
        { title: "Count data points", detail: "More points + stable values = more confidence in the path." },
        { title: "Assess variability", detail: "High variability inside a condition indicates poor control over the behavior; collect more data or revisit conditions." },
        { title: "Identify level", detail: "Note the central value the data cluster around; flag conditions with extreme variability as having no clear level." },
        { title: "Identify trend", detail: "Direction (up/down/zero), degree (gradual/steep), and variability around the trend. Draw a split-middle line when helpful." },
      ],
    },
    {
      title: "Between-condition visual analysis (the 5-dimension check)",
      intro: "Comparing two adjacent conditions.",
      steps: [
        { title: "Level change", detail: "Did the central value shift? Quantify it." },
        { title: "Trend change", detail: "Did slope direction or steepness change?" },
        { title: "Variability change", detail: "Did stability improve or degrade?" },
        { title: "Immediacy of effect", detail: "How fast did the change appear after the condition change line?" },
        { title: "Overlap", detail: "How many points in the new condition fall within the value range of the previous condition? Less overlap = stronger effect." },
      ],
    },
  ],
  examples: [
    { scenario: "An analyst plots tantrum duration each day. After a procedure change, the line drops sharply within two sessions.",
      application: "Strong immediacy + clear level change between conditions; combined with low overlap this supports attributing change to the IV.",
      sourceHint: "Visual analysis of data across conditions determines whether change in level, trend, and/or variability occurred." },
    { scenario: "A teacher uses a cumulative record to show a child the running total of math problems solved.",
      application: "Cumulative record is effective when total responses matter and feedback to the subject is the use case.",
      sourceHint: "Cumulative records are especially effective for displaying data when (a) the total number of responses made over time is important, (b) the graph is used as a source of feedback to the subject." },
    { scenario: "Data points are plotted on a Standard Celeration Chart. A doubling from 10 to 20 takes the same vertical distance as a doubling from 50 to 100.",
      application: "SCC's multiply/divide axis preserves proportional change. The vertical distance for any x2 change is constant.",
      sourceHint: "Ratio charts use a logarithmic-scaled y-axis so that changes in behavior that are of equal proportion (e.g., doublings of the response measure) are represented by equal distances on the vertical axis." },
    { scenario: "Baseline shows a mean of 5 with values ranging 1-9; a single intervention point is 7.",
      application: "High baseline variability prevents inference; collect more baseline data before evaluating the intervention.",
      sourceHint: "A high degree of variability within a given condition usually indicates that little or no control has been achieved over the factors influencing the behavior." },
  ],
  comparisons: [
    {
      title: "Line graph vs Bar graph",
      left:  { label: "Line graph", points: [
        "Continuous x-axis representing time.",
        "Traces change session-by-session.",
        "Default in single-subject design.",
      ]},
      right: { label: "Bar graph", points: [
        "Discrete categories on x-axis (e.g., conditions or participants).",
        "Summarizes and compares.",
        "Loses information about within-condition dynamics.",
      ]},
    },
    {
      title: "Line graph vs Cumulative record",
      left:  { label: "Line graph", points: [
        "Each data point shows the value for that period.",
        "Easy to see level, trend, variability.",
      ]},
      right: { label: "Cumulative record", points: [
        "Each data point shows the running total since the start.",
        "Slope = response rate; flat = no responding.",
        "Best when total responses or fine-grained rate matters.",
      ]},
    },
    {
      title: "Equal-interval scale vs Standard Celeration Chart",
      left:  { label: "Equal-interval", points: [
        "Equal vertical distances = equal additive differences.",
        "Easy to read; standard in most journal figures.",
      ]},
      right: { label: "Standard Celeration Chart", points: [
        "Equal vertical distances = equal proportional changes (x2, x3, ...).",
        "Six cycles cover huge dynamic range (0.01 to 1000+).",
        "Workhorse of precision teaching.",
      ]},
    },
  ],
  misconceptions: [
    {
      myth: "If a line goes up, the treatment worked.",
      reality: "Visual analysis evaluates level, trend, variability, immediacy, and overlap across conditions; a rising line alone proves nothing.",
      sourceHint: "Visual analysis of data across conditions determines whether change in level, trend, and/or variability occurred.",
    },
    {
      myth: "More data points are always better.",
      reality: "More + stable data are better. More + highly variable data still indicates poor control; collect more data only if it produces stability.",
      sourceHint: "As a general rule, the more data in a condition and the greater the stability of those data, the more confidence one can place in the data path's estimate of behavior during that time.",
    },
    {
      myth: "Mean and median lines summarize a condition without loss.",
      reality: "They can obscure variability and trends. Use them as supplements, never as substitutes for the raw data path.",
      sourceHint: "Mean and median level lines should be used and interpreted with care because they can obscure important variability and trends in the data.",
    },
    {
      myth: "Connect every data point in sequence.",
      reality: "Do not connect across condition lines, time discontinuities, missing data, follow-up periods unevenly spaced, or values outside the y-axis range.",
      sourceHint: "Successive data points should not be connected when (a) they fall on either side of a condition change line; (b) they span a significant period in which behavior was not measured.",
    },
    {
      myth: "Statistical tests are required to claim a behavior change.",
      reality: "ABA relies on visual analysis as a conservative judgmental aid. Only effects that produce meaningful, replicable change survive.",
      sourceHint: "Visual analysis of graphed data is a conservative method for determining the significance of behavior change.",
    },
  ],
  examChecklist: [
    "Name the seven major parts of a line graph.",
    "Describe two purposes of a bar graph.",
    "State why slope on a cumulative record equals response rate.",
    "Explain when NOT to connect successive data points.",
    "List the four within-condition visual-analysis properties.",
    "List the five between-condition visual-analysis dimensions.",
    "Distinguish overall response rate from local response rate.",
    "Define level, trend, and variability.",
    "Describe the split-middle line of progress.",
    "Explain why the SCC's y-axis is multiplicative.",
  ],
  flashcardTerms: [
    "Graph", "Data", "Data path",
    "Line graph", "Bar graph", "Cumulative record", "Cumulative recorder",
    "Scatterplot", "Standard Celeration Chart", "Precision teaching",
    "Independent variable", "Dependent variable",
    "Equal-interval scale", "Ratio scale",
    "Overall response rate", "Local response rate",
    "Level", "Variability", "Trend", "Split-middle line of progress", "Visual analysis",
  ],
  quiz: [
    {
      id: "Q1",
      question: "Why does ABA rely on visual analysis rather than statistical significance testing?",
      options: [
        { label: "A", text: "It is faster and imposes no arbitrary alpha; only repeatable, meaningful effects survive." },
        { label: "B", text: "It is more sensitive to weak effects." },
        { label: "C", text: "It is the only legal method for behavior analysis." },
        { label: "D", text: "It eliminates the need for replication." },
      ],
      answer: "A",
      explanation: "Visual analysis is fast, easy to learn, imposes no arbitrary significance level, and is conservative: only meaningful, replicable effects survive scrutiny.",
      topic: "Visual analysis",
      sourceHint: "Visual analysis of graphed data is a conservative method for determining the significance of behavior change.",
    },
    {
      id: "Q2",
      question: "On a cumulative record, what does the SLOPE of the data path represent?",
      options: [
        { label: "A", text: "Variability of responding" },
        { label: "B", text: "Response rate" },
        { label: "C", text: "Baseline level" },
        { label: "D", text: "Trial-by-trial agreement" },
      ],
      answer: "B",
      explanation: "Each cumulative record point is the running total; the steeper the slope, the more responses per unit time, hence higher rate.",
      topic: "Cumulative records",
      sourceHint: "The steeper the slope of the data path on a cumulative graph, the higher the response rate.",
    },
    {
      id: "Q3",
      question: "Which is NOT one of the four within-condition visual-analysis properties?",
      options: [
        { label: "A", text: "Number of data points" },
        { label: "B", text: "Variability" },
        { label: "C", text: "Level" },
        { label: "D", text: "Overlap" },
      ],
      answer: "D",
      explanation: "Overlap is a BETWEEN-condition dimension. Within-condition checks are number of data points, variability, level, and trend.",
      topic: "Within-condition analysis",
      sourceHint: "Visual analysis of data within a given condition focuses on the number of data points, the variability of performance, the level of performance, and the direction and degree of any trends in the data.",
    },
    {
      id: "Q4",
      question: "Which scale type makes a doubling from 10 to 20 the same vertical distance as 50 to 100?",
      options: [
        { label: "A", text: "Equal-interval scale" },
        { label: "B", text: "Ratio (logarithmic) scale" },
        { label: "C", text: "Categorical scale" },
        { label: "D", text: "Standard error scale" },
      ],
      answer: "B",
      explanation: "Ratio (logarithmic) scales display equal proportional changes as equal vertical distances. The Standard Celeration Chart uses this scale.",
      topic: "Scales",
      sourceHint: "Ratio charts use a logarithmic-scaled y-axis so that changes in behavior that are of equal proportion (e.g., doublings of the response measure) are represented by equal distances on the vertical axis.",
    },
    {
      id: "Q5",
      question: "When should you NOT connect two successive data points with a line?",
      options: [
        { label: "A", text: "When they fall on either side of a condition change line." },
        { label: "B", text: "When the second point is higher than the first." },
        { label: "C", text: "When both points fall within the same condition." },
        { label: "D", text: "Never; always connect for readability." },
      ],
      answer: "A",
      explanation: "Do not connect across condition change lines, time discontinuities, missing data periods, or unevenly spaced follow-ups.",
      topic: "Constructing line graphs",
      sourceHint: "Successive data points should not be connected when (a) they fall on either side of a condition change line.",
    },
    {
      id: "Q6",
      question: "A high degree of variability inside a single condition usually signals:",
      options: [
        { label: "A", text: "Excellent experimental control" },
        { label: "B", text: "Little or no control over factors influencing the behavior" },
        { label: "C", text: "A strong treatment effect" },
        { label: "D", text: "Observer drift" },
      ],
      answer: "B",
      explanation: "Within-condition variability indicates that uncontrolled variables are driving behavior; the condition's central tendency is unclear.",
      topic: "Variability",
      sourceHint: "A high degree of variability within a given condition usually indicates that little or no control has been achieved over the factors influencing the behavior.",
    },
    {
      id: "Q7",
      question: "Which is a valid use of a bar graph in ABA?",
      options: [
        { label: "A", text: "Showing session-by-session change in tantrum duration." },
        { label: "B", text: "Comparing average performance across the conditions of an experiment." },
        { label: "C", text: "Plotting cumulative responses across time." },
        { label: "D", text: "Showing the slope of response rate." },
      ],
      answer: "B",
      explanation: "Bar graphs summarize and compare conditions or participants. They are not suited for time-sequential within-condition data.",
      topic: "Graph selection",
      sourceHint: "Bar graphs are used for two primary purposes: (a) to display discrete data not related by an underlying dimension that can be used to scale the horizontal axis and (b) to summarize and enable easy comparison.",
    },
  ],
  reviewPrompts: [
    "Sketch a line graph with two conditions, condition change lines, condition labels, data points, and a figure caption.",
    "Apply the within-condition 4-point check to a sample data set and decide if the level is stable.",
    "Apply the between-condition 5-dimension check to a baseline-intervention pair.",
    "Draw a cumulative record corresponding to a session with bursts of responding followed by long pauses.",
    "Explain when a Standard Celeration Chart is preferable to an equal-interval line graph.",
    "List three graph-construction features that can distort visual analysis and how to detect them.",
  ],
  recap: [
    "Graphs are how behavior analysts see what their numbers mean. Line graphs are the workhorse; bar graphs summarize; cumulative records reveal rate; SCCs preserve proportional change; scatterplots map distributions.",
    "Construction matters: scale axes correctly, draw condition change lines, label conditions, plot solid dots, and never connect points across phases, gaps, or scale discontinuities.",
    "Visual analysis is conservative. Within a condition examine number of points, variability, level, and trend. Across conditions examine change in level, trend, variability, immediacy, and overlap. Only effects that survive this scrutiny are accepted as functional relations.",
  ],
  sourceExcerpts: [
    {
      heading: "Why graphs",
      text: "Graphing each measure of behavior as it is collected provides the practitioner or researcher with an immediate and ongoing visual record of the participant's behavior, allowing treatment and experimental decisions to be responsive to the participant's performance. Direct and continual contact with the data in a readily analyzable format enables the practitioner or researcher to identify and investigate interesting variations in behavior as they occur.",
    },
    {
      heading: "Within-condition analysis",
      text: "Visual analysis of data within a given condition focuses on the number of data points, the variability of performance, the level of performance, and the direction and degree of any trends in the data. As a general rule, the more data in a condition and the greater the stability of those data, the more confidence one can place in the data path's estimate of behavior during that time.",
    },
    {
      heading: "Cumulative records and the SCC",
      text: "Each data point on a cumulative record represents the total number of responses emitted by the subject since measurement began. The steeper the slope of the data path on a cumulative graph, the higher the response rate. The Standard Celeration Chart is a six-cycle multiply-divide graph that enables the standardized charting of celeration, a linear measure of frequency change across time.",
    },
    {
      heading: "Trend",
      text: "Trend refers to the overall direction taken by a data path; trends are described in terms of their direction (increasing, decreasing, or zero trend), degree (gradual or steep), and the extent of variability of data points around the trend.",
    },
  ],
};

export default chapter06Module;
